if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      static getOrCreateStorageValue(key, generator) {
        let value = localStorage.getItem(key);
        if (!value) {
          value = generator();
          localStorage.setItem(key, value);
        }
        return value;
      }

      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            this.initializeProductStats();

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);

            // Re-initialize slider components inside popup after DOM is ready
            setTimeout(() => {
              this.modalContent.querySelectorAll('slider-component').forEach((sc) => {
                sc.slider = sc.querySelector('[id^="Slider-"]');
                sc.sliderItems = sc.querySelectorAll('[id^="Slide-"]');
                sc.prevButton = sc.querySelector('button[name="previous"]');
                sc.nextButton = sc.querySelector('button[name="next"]');
                sc.currentPageElement = sc.querySelector('.slider-counter--current');
                sc.pageTotalElement = sc.querySelector('.slider-counter--total');
                if (sc.slider) {
                  sc.initPages();
                  sc.slider.addEventListener('scroll', sc.update.bind(sc));
                  if (sc.prevButton && sc.nextButton) {
                    sc.prevButton.addEventListener('click', sc.onButtonClick.bind(sc));
                    sc.nextButton.addEventListener('click', sc.onButtonClick.bind(sc));
                  }
                }
              });
              // Re-initialize media-gallery for thumbnail clicks and dot navigation
              this.modalContent.querySelectorAll('media-gallery').forEach((mg) => {
                mg.elements = {
                  liveRegion: mg.querySelector('[id^="GalleryStatus"]'),
                  viewer: mg.querySelector('[id^="GalleryViewer"]'),
                  thumbnails: mg.querySelector('[id^="GalleryThumbnails"]'),
                  dots: mg.querySelectorAll('[data-slider-dot]'),
                };
                if (mg.elements.viewer) {
                  mg.elements.viewer.addEventListener('slideChanged', debounce(mg.onSlideChanged.bind(mg), 500));
                }
                if (mg.elements.thumbnails) {
                  mg.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
                    const btn = mediaToSwitch.querySelector('button');
                    if (btn) btn.addEventListener('click', mg.setActiveMedia.bind(mg, mediaToSwitch.dataset.target, false));
                  });
                }
                if (mg.elements.dots?.length) {
                  const ul = mg.elements.viewer.querySelector('[id^="Slider-Gallery-"]');
                  const slides = ul ? Array.from(ul.children) : [];
                  mg.elements.dots.forEach((dot) => {
                    dot.addEventListener('click', () => {
                      const idx = Number(dot.dataset.index || 0);
                      const target = slides[idx];
                      if (!target) return;
                      ul.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
                      mg.setActiveMedia(target.dataset.mediaId, false);
                    });
                  });
                }

                // Hiển thị ngay ảnh đầu tiên khi mở popup (không cần bấm thumbnail).
                if (typeof mg.setActiveMedia === 'function') {
                  const isVisible = (el) => el && el.offsetParent !== null && el.style.display !== 'none';
                  const viewerItems = mg.elements.viewer
                    ? Array.from(mg.elements.viewer.querySelectorAll('[data-media-id]'))
                    : [];
                  let firstId = (viewerItems.find(isVisible) || viewerItems[0])?.dataset.mediaId;
                  if (!firstId) {
                    const firstThumb = mg.elements.thumbnails?.querySelector('[data-target]');
                    firstId = firstThumb?.dataset.target;
                  }
                  if (firstId) mg.setActiveMedia(firstId, false);
                }
              });
            }, 150);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      initializeProductStats() {
        // Đã gỡ số liệu ảo (reviews/sold/viewers/stock sinh ngẫu nhiên) khỏi theme
        return;
        const productInfo = this.modalContent.querySelector('product-info');
        const productKey = productInfo?.dataset?.productId;
        if (!productKey) return;

        const getOrCreate = QuickAddModal.getOrCreateStorageValue;
        const reviewCount = getOrCreate(`review_${productKey}`, () => Math.floor(Math.random() * 201) + 100);
        const soldCount = getOrCreate(`sold_${productKey}`, () => (Math.random() * (2.9 - 1.1) + 1.1).toFixed(1));
        const viewerCount = getOrCreate(`viewer_${productKey}`, () => Math.floor(Math.random() * 26) + 15);

        const stockKey = `stock_${productKey}`;
        let stockCount = localStorage.getItem(stockKey);

        if (!stockCount || stockCount < 3 || stockCount > 9) {
          stockCount = Math.floor(Math.random() * 7) + 3;
          localStorage.setItem(stockKey, stockCount);
        } else {
          stockCount = parseInt(stockCount, 10);
        }

        this.modalContent.querySelectorAll('.rating-count').forEach((element) => {
          element.innerText = `(${reviewCount} REVIEWS)`;
        });

        this.modalContent.querySelectorAll('.sold-badge').forEach((element) => {
          element.innerText = `${soldCount}K+ SOLD`;
        });

        this.modalContent.querySelectorAll('.viewer-count').forEach((element) => {
          element.innerText = viewerCount;
        });

        const updateStockUI = () => {
          this.modalContent.querySelectorAll('.stock-count').forEach((element) => {
            element.innerText = `${stockCount} left in stock`;
          });
        };

        updateStockUI();

        const addToCartBtn = this.modalContent.querySelector('.product-form__submit');
        if (!addToCartBtn || addToCartBtn.dataset.stockBound === 'true') return;

        addToCartBtn.dataset.stockBound = 'true';
        addToCartBtn.addEventListener('click', () => {
          if (stockCount > 1) {
            stockCount -= 1;
            localStorage.setItem(stockKey, stockCount);
            updateStockUI();
          }
        });
      }
    }
  );
}
